import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock } from "lucide-react";
import {
  isCrawlerJob,
  getCompanyName,
  getCompanyLogoUrl,
  getSourceName,
  getSourceType,
} from "@/lib/jobUtils";
import { getCompanyInitials, getCompanyColor } from "@/lib/companyAvatar";
import { timeAgo } from "@/lib/timeAgo";
import { BookmarkButton } from "./BookmarkButton";
import { WorkModelBadge } from "./WorkModelBadge";
import { DeadlineBadge } from "./DeadlineBadge";
import { SourceBadge } from "./SourceBadge";

const employmentTypeLabels: Record<string, string> = {
  aprendiz: "Jovem Aprendiz",
  estagio: "Estágio",
  primeiro_emprego: "Primeiro Emprego",
  assistente: "Assistente",
  analista_junior: "Analista Junior",
  freelancer: "Freelancer",
  junior: "Júnior",
};

const workModelLabels: Record<string, string> = {
  presencial: "Presencial",
  hibrido: "Híbrido",
  remoto: "Remoto",
};

interface JobCardProps {
  job: any;
  /** Whether the current user has bookmarked this job */
  isSaved?: boolean;
  /** Callback to toggle bookmark (only shown when provided) */
  onToggleSave?: (jobId: string) => void;
  /** Override link prefix (default: /oportunidades) */
  linkPrefix?: string;
}

export default function JobCard({ job, isSaved = false, onToggleSave, linkPrefix = "/oportunidades" }: JobCardProps) {
  const companyName = getCompanyName(job);
  const logoUrl = getCompanyLogoUrl(job);
  const tags: string[] = Array.isArray(job.skills_tags) ? job.skills_tags : [];
  const isCrawler = isCrawlerJob(job);
  const sourceName = getSourceName(job);
  const sourceType = getSourceType(job);
  const initials = getCompanyInitials(companyName);
  const avatarColor = getCompanyColor(companyName);
  const publishedTime = timeAgo(job.published_at || job.created_at);

  return (
    <Link to={`${linkPrefix}/${job.id}`}>
      <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer group">
        <CardContent className="p-5">
          {/* Top row: source + bookmark + time */}
          <div className="flex items-center justify-between mb-2">
            {isCrawler && sourceName ? (
              <SourceBadge sourceName={sourceName} sourceType={sourceType} size="sm" />
            ) : (
              <span />
            )}
            <div className="flex items-center gap-1">
              {onToggleSave && (
                <BookmarkButton
                  isSaved={isSaved}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onToggleSave(job.id);
                  }}
                />
              )}
              {publishedTime && (
                <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {publishedTime}
                </span>
              )}
            </div>
          </div>

          {/* Title */}
          <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2 mb-2">
            {job.title}
          </h3>

          {/* Company */}
          <div className="flex items-center gap-2 text-muted-foreground mb-3">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt=""
                className="w-6 h-6 rounded object-contain shrink-0 bg-muted"
              />
            ) : initials ? (
              <span
                className={`w-6 h-6 rounded flex items-center justify-center text-[10px] font-bold text-white shrink-0 ${avatarColor}`}
              >
                {initials}
              </span>
            ) : (
              <span className="w-6 h-6 rounded bg-muted shrink-0" />
            )}
            <span className="text-sm truncate">{companyName}</span>
          </div>

          {/* Badges: type, model, deadline, location */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            <Badge variant="secondary" className="text-xs">
              {employmentTypeLabels[job.employment_type] || job.employment_type}
            </Badge>
            <WorkModelBadge model={job.work_model} size="sm" />
            <DeadlineBadge deadline={job.deadline_date} size="sm" />
            {job.location_state && (
              <Badge variant="outline" className="text-xs">
                {job.location_city
                  ? `${job.location_city}, ${job.location_state}`
                  : job.location_state}
              </Badge>
            )}
          </div>

          {/* Description summary */}
          {job.description_summary && (
            <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
              {job.description_summary}
            </p>
          )}

          {/* Skills tags */}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="text-[11px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium"
                >
                  {tag}
                </span>
              ))}
              {tags.length > 3 && (
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">
                  +{tags.length - 3}
                </span>
              )}
            </div>
          )}

          {/* Stipend */}
          {(job.stipend_min || job.stipend_max) && (
            <p className="text-sm font-medium text-primary">
              {job.stipend_max &&
              job.stipend_min &&
              job.stipend_max !== job.stipend_min
                ? `R$ ${job.stipend_min.toLocaleString()} - R$ ${job.stipend_max.toLocaleString()}`
                : `R$ ${(job.stipend_min || job.stipend_max)?.toLocaleString()}`}
              /mês
            </p>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

export { employmentTypeLabels, workModelLabels };
